import Avatar from '@jg/avatar';
import cn from 'classnames';
import queryString from 'query-string';
import React from 'react';
import { useTranslation } from 'react-i18next';
import calendarIcon from './calendarIcon.svg';
import mapPinIcon from './mapPinIcon.svg';
import Meta from './Meta';

type OwnerImg = {
  type: 'avatar' | 'logo';
  url?: string;
};

export interface Props {
  url: string;
  img: string;
  imgAspectRatio?: 'crowdfunding' | 'fundraising';
  title: string;
  subtitle?: string;
  event?: string;
  location?: string;
  owner: {
    name: string;
    img?: OwnerImg;
  };
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
}

export default React.memo(function ResultCard({
  url,
  img,
  imgAspectRatio = 'crowdfunding',
  title,
  subtitle,
  event,
  location,
  owner,
  onClick,
}: Props) {
  const { t } = useTranslation();

  return (
    <div
      className={cn('jg-search-result-card', 'jg-display-flex jg-flex-column')}
      data-testid="jg-search-result-card"
    >
      <a
        href={url}
        onClick={onClick}
        className={cn(
          'jg-search-result-card__inner',
          'jg-text-no-underline@hover',
          'jg-background--white-smoke@hover',
          'jg-space-pxs jg-space-mbsm',
          'jg-space-pn@md jg-space-msm@md',
          'jg-br jg-bd@md jg-bd--very-light-grey@md',
          'jg-flex-g1 jg-display-flex jg-flex-column'
        )}
      >
        <div
          className={cn(
            'jg-search-result-card__image-ratio-wrapper',
            {
              'jg-search-result-card__image-ratio-wrapper--crowdfunding':
                imgAspectRatio === 'crowdfunding',
            },
            'jg-position-relative jg-space-mbxs jg-space-mbn@md'
          )}
        >
          {[
            'jg-search-result-card__image jg-search-result-card__image--bg',
            'jg-search-result-card__image',
          ].map(classNames => (
            <div
              key={classNames}
              className={cn(
                'jg-search-result-card__image-wrapper',
                'jg-position-absolute jg-pin',
                'jg-bd jg-bd--very-light-grey',
                'jg-bdhn@md jg-bdtn@md',
                'jg-display-flex jg-flex-aic jg-flex-jcc'
              )}
            >
              <img
                className={cn(classNames, 'lazyload')}
                data-src={`${
                  queryString.parseUrl(img).url
                }?template=CampaignHeroSmall`}
                alt=""
              />
            </div>
          ))}
        </div>

        <div className="jg-space-psm@md jg-flex-g1 jg-display-flex jg-flex-column">
          <div className="jg-flex-g1">
            <h3
              className={cn(
                'jg-search-result-card__title',
                'jg-text--large-regular jg-color--nero jg-space-mbxs'
              )}
            >
              {title}
            </h3>

            {subtitle && <Meta text={subtitle} />}
            {event && <Meta icon={calendarIcon} text={event} />}
            {location && <Meta icon={mapPinIcon} text={location} />}
          </div>

          <div
            aria-label={t('resultCard.pageOwnerAriaLabel', 'Page owner')}
            className={cn(
              'jg-display-flex jg-flex-aic',
              'jg-space-mtxs@md',
              'jg-space-mtsm@lg',
              'jg-text--medium jg-color--nero'
            )}
          >
            {owner.img && owner.img.type === 'avatar' && (
              <div className="jg-flex-g0 jg-flex-s0 jg-space-mrxs">
                <Avatar picture={owner.img.url} />
              </div>
            )}

            {owner.img && owner.img.type === 'logo' && owner.img.url && (
              <div className="jg-flex-g0 jg-flex-s0 jg-space-mrxs">
                <div
                  className={cn(
                    'jg-icon--medium jg-bd jg-bd--very-light-grey',
                    'jg-display-flex jg-flex-aic jg-flex-jcc'
                  )}
                >
                  <img
                    className="jg-search-result-card__owner-image--logo lazyload"
                    data-src={owner.img.url}
                    alt=""
                  />
                </div>
              </div>
            )}

            <div
              className={cn(
                'jg-search-result-card__owner-name',
                'jg-flex-g1 jg-flex-s1 jg-whitespace-no-wrap'
              )}
            >
              {owner.name}
            </div>
          </div>
        </div>
      </a>
    </div>
  );
});
